<template>
  <div class="p-2">
    <div class="mb-4">
      <button
        type="button"
        class="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        @click="goBackToMarketing"
      >
        <svg
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          class="-ml-1 mr-2 h-5 w-5"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M10 19l-7-7m0 0l7-7m-7 7h18"
          ></path>
        </svg>
        Back to Campaigns
      </button>
    </div>

    <div v-if="campaign" class="mb-5">
      <div class="flex items-center gap-3">
        <h2 class="text-2xl font-semibold text-gray-900">
          {{ campaign.name }}
        </h2>
        <span
          class="Polaris-Badge"
          :class="getCampaignBadgeTone(campaign.status)"
        >
          <span class="Polaris-Text--root Polaris-Text--visuallyHidden">
            {{ getCampaignBadgeA11yTone(campaign.status) }}
          </span>
          <span class="Polaris-Text--root Polaris-Text--bodySm">
            {{ getCampaignStatus(campaign.status) }}
          </span>
        </span>
      </div>

      <div class="mt-4 bg-white overflow-hidden shadow rounded-lg">
        <div class="px-4 py-5 sm:p-6">
          <h4 class="text-lg leading-6 font-medium text-gray-900 mb-4">
            Campaign details
          </h4>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p class="text-xs text-gray-500 uppercase">Campaign name</p>
              <p class="text-sm text-gray-900">{{ campaign.name }}</p>
            </div>

            <div>
              <p class="text-xs text-gray-500 uppercase">Created at</p>
              <p class="text-sm text-gray-900">
                {{ formatTimestamp(campaign.createdAt) }}
              </p>
            </div>
            <div>
              <p class="text-xs text-gray-500 uppercase">Scheduled at</p>
              <p class="text-sm text-gray-900">
                {{ formatTimestamp(campaign.scheduledAt) }}
              </p>
            </div>

            <div>
              <p class="text-xs text-gray-500 uppercase">Audience</p>
              <p class="text-sm text-gray-900">
                {{ campaign.segmentId || "-" }}
              </p>
            </div>
            <div>
              <p class="text-xs text-gray-500 uppercase">Button text</p>
              <p class="text-sm text-gray-900">
                {{ campaign.buttonText || "-" }}
              </p>
            </div>
            <div>
              <p class="text-xs text-gray-500 uppercase">Button URL</p>
              <p class="text-sm text-gray-900 break-all">
                {{ campaign.buttonUrl || "-" }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <h3 class="text-lg leading-6 font-medium text-gray-900 mt-5">
      Campaign Stats
    </h3>
    <div class="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-3">
      <div class="bg-white overflow-hidden shadow rounded-lg">
        <div class="px-4 py-5 sm:p-6">
          <dl>
            <dt class="text-sm leading-5 font-medium text-gray-500 truncate">
              Total Subscribers
            </dt>
            <dd class="mt-1 text-3xl leading-9 font-semibold text-gray-900">
              {{ report.totalSubscribers }}
            </dd>
          </dl>
        </div>
      </div>

      <div class="bg-white overflow-hidden shadow rounded-lg">
        <div class="px-4 py-5 sm:p-6">
          <dl>
            <dt class="text-sm leading-5 font-medium text-gray-500 truncate">
              Sent
            </dt>
            <dd class="mt-1 text-3xl leading-9 font-semibold text-gray-900">
              {{ report.sentSubscribers }}
            </dd>
          </dl>
        </div>
      </div>
      <div class="bg-white overflow-hidden shadow rounded-lg">
        <div class="px-4 py-5 sm:p-6">
          <dl>
            <dt class="text-sm leading-5 font-medium text-gray-500 truncate">
              Failed
            </dt>
            <dd class="mt-1 text-3xl leading-9 font-semibold text-gray-900">
              {{ report.failedSubscribers }}
            </dd>
          </dl>
        </div>
      </div>
      <div class="bg-white overflow-hidden shadow rounded-lg relative">
        <div class="px-4 py-5 sm:p-6">
          <dl>
            <dt
              class="text-sm leading-5 font-medium text-gray-500 truncate flex items-center"
            >
              Orders Generated
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-4 w-4 text-gray-500 hover:text-gray-700 cursor-pointer ml-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                @mouseenter="showPopover = true"
                @mouseleave="showPopover = false"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>
              <div
                v-if="showPopover"
                class="absolute z-10 px-2 py-1 -mt-1 text-sm bg-gray-700 text-white rounded shadow-lg"
                style="white-space: break-spaces"
              >
                Orders placed by customers within 7 days of receiving the
                campaign message
              </div>
            </dt>
            <dd class="mt-1 text-3xl leading-9 font-semibold text-gray-900">
              <span v-if="isLoading">
                <Spinner />
              </span>
              <span v-else>
                {{ orders.length }}
              </span>
            </dd>
          </dl>
        </div>
      </div>
      <div class="bg-white overflow-hidden shadow rounded-lg">
        <div class="px-4 py-5 sm:p-6">
          <dl>
            <dt class="text-sm leading-5 font-medium text-gray-500 truncate">
              Total Order value from the campaign
            </dt>
            <dd class="mt-1 text-3xl leading-9 font-semibold text-gray-900">
              <span v-if="isLoading">
                <Spinner />
              </span>
              <span v-else>{{ totalOrderValue }}</span>
            </dd>
          </dl>
        </div>
      </div>
    </div>

    <div
      class="mt-5 bg-white overflow-hidden shadow rounded-lg"
      v-if="template"
    >
      <div class="px-4 py-5 sm:p-6">
        <h4 class="text-lg leading-6 font-medium text-gray-900">Message</h4>
        <p class="mt-2 text-sm text-gray-600">{{ template.message }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import Spinner from "vue-simple-spinner";

export default {
  components: {
    Spinner
  },
  data() {
    return {
      report: {},
      template: null,
      campaign: null,
      orders: [],
      showPopover: false,
      totalOrderValue: 0,
      isLoading: false
    };
  },
  created() {
    const campaignId = this.$route.params.campaignId;
    this.$store
      .dispatch("Marketing/getCampaignReport", { campaignId })
      .then((response) => {
        this.report = response.report;
        this.template = response.template;
        this.campaign = response.campaign;
      });
    this.getOrderReport();
  },
  methods: {
    goBackToMarketing() {
      this.$router.push({ name: "Campaigns" });
    },
    formatTimestamp(value) {
      if (!value) return "-";
      const date = new Date(value * 1000);
      return date.toLocaleString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
        month: "short",
        day: "2-digit"
      });
    },
    getCampaignStatus(status) {
      if (status === "waiting_for_approval") {
        return "Waiting for approval";
      }
      if (!status) return "-";
      return status.replace(/_/g, " ");
    },
    getCampaignBadgeTone(status) {
      if (status === "waiting_for_approval") {
        return "Polaris-Badge--toneWarning";
      }
      if (status === "scheduled" || status === "approved")
        return "Polaris-Badge--toneSuccess";
      if (status === "draft") return "Polaris-Badge--toneInfo";
      return "";
    },
    getCampaignBadgeA11yTone(status) {
      if (status === "waiting_for_approval") return "Warning";
      if (status === "scheduled" || status === "approved") return "Success";
      if (status === "draft") return "Info";
      return "Status";
    },
    getOrderReport() {
      this.isLoading = true;
      const campaignId = this.$route.params.campaignId;
      this.$store
        .dispatch("Marketing/getOrderStats", { campaignId })
        .then((response) => {
          this.isLoading = false;
          console.log("response", response);
          this.orders = response.orderStats.orderNumbers;
          if (response.orderStats.orderNumbers.length > 0) {
            this.totalOrderValue = response.orderStats.totalPrice;
          }
        });
    }
  }
};
</script>

<style lang="scss" scoped></style>
